import { featured, featuredTags } from "@/mocks/home";
import SectionHeader from "./SectionHeader";

const toneMap: Record<string, string> = {
  coral: "bg-primary-500 text-background-50 ring-primary-200",
  amber: "bg-accent-500 text-background-50 ring-accent-200",
  tan: "bg-secondary-400 text-foreground-50 ring-secondary-200",
};

const toneBadge: Record<string, string> = {
  coral: "bg-primary-100 text-primary-700",
  amber: "bg-accent-100 text-accent-800",
  tan: "bg-secondary-100 text-secondary-800",
};

const BANNER =
  "https://readdy.ai/api/search-image?query=Wide%20editorial%20illustration%20banner%20of%20a%20six%20node%20journey%20path%20with%20small%20icons%20for%20going%20live%20viral%20spike%20controversy%20reversal%20and%20exit%20connected%20by%20a%20flowing%20red%20ribbon%2C%20warm%20coral%20red%20and%20terracotta%20with%20soft%20amber%20and%20warm%20cream%20flat%20vector%20style%20on%20plain%20cream%20background%2C%20minimal%20clean%20composition%20with%20clear%20flow%20direction%2C%20high%20detail%2C%20landscape%20layout&width=1280&height=640&seq=feature-banner-69&orientation=landscape";

export default function TimelineFeature() {
  return (
    <section id="feature" className="bg-background-50">
      <div className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-20">
        <SectionHeader
          index="02"
          kicker="本期专题"
          title="一张脉络图讲清前因后果"
          desc="以热度最高的「素人一夜走红又退网」为例，把完整经过切成六个阶段、逐节点对齐信源，时间线一目了然。"
          href="#grid"
          linkLabel="更多专题"
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[400px_1fr]">
          {/* sticky intro / 脉络卡 */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <div className="overflow-hidden rounded-xl border border-background-200 bg-background-100">
              <div className="relative">
                <img
                  src={BANNER}
                  alt="素人爆红退网事件脉络"
                  className="aspect-[2/1] w-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 to-transparent" />
              </div>
              <div className="p-5">
                <div className="flex flex-wrap gap-2">
                  {featuredTags.map((t) => (
                    <span
                      key={t}
                      className="rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-semibold text-accent-800 whitespace-nowrap"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <h3 className="mt-3 font-heading text-2xl leading-snug text-foreground-950">
                  {featured.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{featured.intro}</p>

                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 border-t border-background-200 pt-4 text-xs text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-link-m text-primary-600" /> 首发信源 28
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-loop-left-line text-primary-600" /> 口径 2 方存疑
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-check-double-line text-primary-600" /> 已完结
                  </span>
                </div>

                <a
                  href="#faq"
                  className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                >
                  展开完整时间线
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </div>

          {/* timeline stack */}
          <ol className="relative space-y-5">
            {featured.phases.map((p, i) => (
              <li key={p.step} className="relative flex gap-4 md:gap-6">
                {/* rail + dot */}
                <div className="relative flex flex-col items-center">
                  <span
                    className={`relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold ring-4 ${toneMap[p.tone]}`}
                  >
                    {i + 1}
                  </span>
                  {i < featured.phases.length - 1 && (
                    <span className="w-px flex-1 bg-background-300" />
                  )}
                </div>

                <div className="mb-1 min-w-0 flex-1 rounded-xl border border-background-200 bg-background-100/70 p-4 transition-colors hover:border-primary-300 md:p-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold whitespace-nowrap ${toneBadge[p.tone]}`}
                    >
                      {p.step}
                    </span>
                    <span className="font-label text-xs font-semibold text-foreground-400">
                      {p.time}
                    </span>
                    <span className="hidden text-[11px] text-foreground-400 sm:inline">
                      · {featured.title.split("，")[0]}
                    </span>
                  </div>
                  <h4 className="mt-2 font-semibold text-foreground-950">{p.title}</h4>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{p.text}</p>
                </div>
              </li>
            ))}

            {/* bottom summary banner */}
            <div className="flex items-center gap-3 rounded-xl bg-accent-500 p-4 text-background-50 md:p-5">
              <i className="ri-mental-health-line text-2xl" />
              <p className="text-sm leading-relaxed">
                复盘要点：从「被看见」到「被考古」，全程仅 10 天。完整经过与各方口径原始出处，
                均可展开逐条回看。
              </p>
            </div>
          </ol>
        </div>
      </div>
    </section>
  );
}